import { useState } from 'react';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { StyledRoleElement } from '../../../atoms/UserPanelAddRole/StyledRoleElement';
import {
    StyledHeading,
    StyledArrow,
    StyledDataWrapper,
    StyledHeader,
    StyledHeaderElement,
    StyledInputWrapper,
} from '../../../../styles/sharedStyles';
import { StyledHeadingCheckbox } from '../../../atoms/SettlementsDoneElement/StyledHeadingCheckbox';
import { StyledText } from '../../../atoms/Text/StyledText';
import { StyledDeleteCheckbox } from '../../../atoms/UserPanelAddRole/StyledDeleteCheckbox';
import { StyledIsOpenWrapper } from '../../../atoms/UserPanelAddRole/StyledIsOpenWrapper';
import { StyledInputsButtonsWrapper } from '../../../atoms/TrackHours/StyledInputsButtonsWrapper';

import { getString } from '../../../../strings';
import useScrollBar from '../../../../logic/hooks/useScrollBar';
import {
    StyledDataPermissionWrapper,
    StyledPermissionsRowElement,
} from '../UserPanelPermissions.styles';
import CloseIcon from '../../../atoms/UserPanelStudens/CloseIcon';
import PaymentsPopup from '../../Payments/PaymentsPopup';
import { assignPermissionToRole, deletePermissionFromRole } from '../../../../logic/requests/permissions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    StyledForm,
    StyledLeftBottomBtn,
} from '../../../molecules/UserPanelEmployeeDataElement/UserPanelEmployeeDataElement.styles';
import { listRoles } from '../../../../logic/requests/students';
import { StyledInputsDivWrapper } from '../../../atoms/StyledInputsDivWrapper/StyledInputsDivWrapper';
import { formSchema } from './PermissionElement.data';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

const PermissionElement = ({ permission, trigger }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { scrollElement, isScrollBar } = useScrollBar(false, true);
    const [confirmPopup, setConfirmPopup] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [isOpenForm, setIsOpenForm] = useState(false);
    const queryClient = useQueryClient();
    const { data: roles } = useQuery(['roles', 'all'], () => listRoles());
    const [isError, setIsError] = useState({ showError: false, message: '' });

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
    } = useForm({
        resolver: yupResolver(formSchema),
    });

    const handleDeletePermissionForRole = () => {
        deletePermissionFromRole(roleToDelete, permission.permission).then(() => {
            trigger();
            queryClient.invalidateQueries(['permissions', 'all']);
            setConfirmPopup(false);
            setRoleToDelete(null);
        });
    };

    const handleOpen = () => {
        trigger();
        setIsOpen(!isOpen);
    };

    const onSubmit = (data) => {
        assignPermissionToRole(data.role, permission.permission).then(() => {
            trigger();
            queryClient.invalidateQueries(['permissions', 'all']);
            setIsOpenForm(false);
            reset();
            setIsError({ showError: false, message: '' });
        }).catch((error) => {
            setIsError({ showError: true, message: error.response?.data?.message || 'An error occurred' });
        });
    };

    return (
        <StyledRoleElement isopen={isOpen}>
            {confirmPopup && (
                <PaymentsPopup
                    title={
                        getString(
                            'UserPanelPermissions_are_you_sure_you_want_to_remove_permission_for_role1'
                        ) +
                        ` ${getString(`roles_${roleToDelete}`)}  ${getString(
                            'UserPanelPermissions_are_you_sure_you_want_to_remove_permission_for_role2'
                        )} ${permission.title}?`
                    }
                    onClose={handleDeletePermissionForRole}
                    cancelTag={getString('student_payments_element_cancel')}
                    closeTag={getString('student_payments_element_yes')}
                    onCancel={() => setConfirmPopup(false)}
                ></PaymentsPopup>
            )}
            <StyledHeading onClick={handleOpen}>
                <StyledText
                    hasdeclaredfontsize="22px"
                    hasdeclaredfontweight="700"
                    hasdeclaredtextalign="center"
                    hasdeclaredtexttransform="uppercase"
                    hasdeclaredfontcolor="#6786BE"
                    hasdeclaredpadding="0 0 0 50px"
                >
                    {permission.title}
                </StyledText>
                <StyledHeadingCheckbox>
                    <StyledArrow isopen={isOpen} />
                </StyledHeadingCheckbox>
            </StyledHeading>
            {isOpen ? (
                <StyledDataWrapper>
                    <ScrollSync>
                        <>
                            <StyledText
                                hasdeclaredfontsize="18px"
                                hasdeclaredtextalign="center"
                                hasdeclaredlineheight="1.4em"
                            >
                                {permission.description}
                            </StyledText>
                            <StyledIsOpenWrapper>
                                <ScrollSyncPane>
                                    <StyledHeader $isscrollbar={isScrollBar} ref={scrollElement}>
                                        <div>
                                            <StyledHeaderElement $hasWidth="100%">
                                                <StyledText
                                                    hasdeclaredfontsize="16px"
                                                    hasdeclaredfontweight="700"
                                                    hasdeclaredtextalign="center"
                                                    hasdeclaredlineheight="1.4em"
                                                >
                                                    {getString('UserPanelPermissions_role')}
                                                </StyledText>
                                            </StyledHeaderElement>
                                        </div>
                                    </StyledHeader>
                                </ScrollSyncPane>
                                <StyledDeleteCheckbox isbgcolor />
                            </StyledIsOpenWrapper>
                            <div
                                style={{
                                    marginTop: '5px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '15px',
                                }}
                            >
                                {permission.roles.map((role) => (
                                    <StyledPermissionsRowElement key={role} $minusWidth={50}>
                                        <ScrollSyncPane>
                                            <div>
                                                <StyledDataPermissionWrapper>
                                                    <StyledHeaderElement $hasWidth="100%">
                                                        <StyledText
                                                            hasdeclaredfontsize="16px"
                                                            hasdeclaredfontweight="600"
                                                            hasdeclaredtextalign="center"
                                                            hasdeclaredlineheight="1.4em"
                                                        >
                                                            {getString(`roles_${role}`)}
                                                        </StyledText>
                                                    </StyledHeaderElement>
                                                </StyledDataPermissionWrapper>
                                            </div>
                                        </ScrollSyncPane>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: '40px',
                                                minHeight: '100%',
                                            }}
                                        >
                                            <StyledDeleteCheckbox
                                                onClick={() => {
                                                    setRoleToDelete(role);
                                                    setConfirmPopup(true);
                                                }}
                                            >
                                                <CloseIcon />
                                            </StyledDeleteCheckbox>
                                        </div>
                                    </StyledPermissionsRowElement>
                                ))}
                            </div>
                        </>
                    </ScrollSync>
                    <ScrollSync>
                        <>
                            {isOpenForm && (
                                <>
                                    <StyledForm onSubmit={handleSubmit(onSubmit)}>
                                        <StyledInputsDivWrapper>
                                            <StyledInputWrapper
                                                error={errors.subject}
                                                hasdeclaredwidth="50%"
                                            >
                                                <label htmlFor="subject">
                                                    <StyledText
                                                        hasdeclaredfontsize="20px"
                                                        hasdeclaredtextalign="center"
                                                        hasdeclaredpadding="8px 0 8px 0"
                                                        hasdeclaredfontweight="600"
                                                    >
                                                        {getString(
                                                            'UserPanelPermissions_role'
                                                        )}
                                                    </StyledText>
                                                </label>
                                                <select id="role" {...register('role')}>
                                                    <option />
                                                    {roles.map((role) => (
                                                        <option key={role} value={role}>
                                                            {getString(`roles_${role}`)}
                                                        </option>
                                                    ))}
                                                </select>
                                                <StyledText
                                                    as="h4"
                                                    hasdeclaredfontsize="16px"
                                                    hasdeclaredfontcolor="#F74017"
                                                    hasdeclaredtextalign="center"
                                                    hasdeclaredfontweight="700"
                                                    hasdeclaredtexttransform="uppercase"
                                                    hasdeclaredlineheight="1.4em"
                                                >
                                                    {getString(
                                                        'user_panel_employee_data_element_przedmiot_jest_wymagany'
                                                    )}
                                                </StyledText>
                                            </StyledInputWrapper>
                                        </StyledInputsDivWrapper>
                                        <StyledInputsButtonsWrapper hasmargin="18px 0 0 0">
                                            <input
                                                type="submit"
                                                value={getString(
                                                    'User_panel_employee_data_element_zapisz'
                                                )}
                                            />
                                            <input
                                                type="reset"
                                                onClick={() => setIsOpenForm(false)}
                                                value={getString(
                                                    'User_panel_employee_data_element_anuluj'
                                                )}
                                            />
                                        </StyledInputsButtonsWrapper>
                                        {isError.showError ? (
                                            <div style={{ width: '100%', marginTop: '25px' }}>
                                                <StyledText
                                                    as="h4"
                                                    hasdeclaredfontsize="16px"
                                                    hasdeclaredfontcolor="#F74017"
                                                    hasdeclaredtextalign="center"
                                                    hasdeclaredfontweight="700"
                                                    hasdeclaredtexttransform="uppercase"
                                                    hasdeclaredlineheight="1.4em"
                                                >
                                                    {isError.message}
                                                </StyledText>
                                            </div>
                                        ) : null}
                                    </StyledForm>
                                </>
                            )}
                            <StyledLeftBottomBtn onClick={() => setIsOpenForm(!isOpenForm)}>
                                <svg
                                    width="36"
                                    height="36"
                                    viewBox="0 0 36 36"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M33.0105 14.2474L21.7527 14.2474L21.7527 2.98966C21.7527 1.60824 20.6324 0.487935 19.251 0.487936L16.7493 0.487936C15.3679 0.487937 14.2476 1.60824 14.2476 2.98966L14.2476 14.2474L2.98982 14.2474C1.6084 14.2474 0.488099 15.3677 0.488099 16.7491V19.2509C0.488099 20.6323 1.6084 21.7526 2.98982 21.7526L14.2476 21.7526L14.2476 33.0103C14.2476 34.3918 15.3679 35.5121 16.7493 35.5121L19.251 35.5121C20.6324 35.5121 21.7527 34.3918 21.7527 33.0103L21.7527 21.7526L33.0105 21.7526C34.3919 21.7526 35.5122 20.6323 35.5122 19.2509V16.7491C35.5122 15.3677 34.3919 14.2474 33.0105 14.2474Z"
                                        fill="white"
                                    />
                                </svg>
                            </StyledLeftBottomBtn>
                        </>
                    </ScrollSync>
                </StyledDataWrapper>
            ) : null}
        </StyledRoleElement>
    );
};

export default PermissionElement;
